import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Target, Award } from "lucide-react";
import EkgLine from "@/components/EkgLine";
import Navbar from "@/components/Navbar";
import NinjaBadge from "@/components/NinjaBadge";

const RANKS = [
  { name: "Student Ninja", color: "text-slate-200" },
  { name: "EKG Apprentice", color: "text-yellow-300" },
  { name: "Shadow Genin", color: "text-orange-300" },
  { name: "Rhythm Ninja", color: "text-green-400" },
  { name: "Ischemia Warrior", color: "text-blue-400" },
  { name: "Arrhythmia Sensei", color: "text-purple-300" },
  { name: "Cardiology Jonin", color: "text-amber-500" },
  { name: "Cardiology Kage", color: "text-red-400 text-glow-red" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-ekg-grid opacity-40" />
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-32 opacity-70">
          <EkgLine className="w-full h-full" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 grid lg:grid-cols-[1fr_auto] gap-12 items-center">
          <div className="max-w-3xl reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-green-500/30 bg-green-500/5 mb-6">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-widest text-green-300">
                Live EKG Training Dojo
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
              Learn to read <span className="text-green-400 text-glow-green">EKGs</span> like a{" "}
              <span className="text-red-400 text-glow-red">ninja</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              From your first squiggle to twelve-lead mastery. Eight belts, real
              EKGs, and quizzes that actually make you a baby cardiologist.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta-start">
                <Button
                  size="lg"
                  className="rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold px-8 h-12"
                >
                  Begin Training
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-login">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white h-12 px-6"
                >
                  I already train here
                </Button>
              </Link>
            </div>
          </div>

          {/* Ninja belt stack visual */}
          <div className="hidden lg:block relative w-[300px] h-[420px] reveal" style={{ animationDelay: "150ms" }}>
            <div className="absolute inset-0">
              {[8, 6, 4, 2].map((lvl, idx) => (
                <div
                  key={lvl}
                  className="absolute"
                  style={{
                    top: `${idx * 90}px`,
                    left: idx % 2 === 0 ? "20px" : "140px",
                    transform: `rotate(${idx % 2 === 0 ? -6 : 6}deg)`,
                  }}
                >
                  <NinjaBadge level={lvl} size={140} glow={lvl === 8} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Zap,
              title: "Bite-sized lessons",
              body: "Every level is 2–4 short lessons. No walls of text. Learn a concept, see an EKG, move on.",
            },
            {
              icon: Target,
              title: "Quiz to advance",
              body: "Score 80%+ to unlock the next belt. Mixed MCQ and EKG identification questions.",
            },
            {
              icon: Award,
              title: "Rank up 8 belts",
              body: "Journey from Student Ninja to Cardiology Kage. Your progress persists across devices.",
            },
          ].map((f, i) => (
            <div
              key={f.title}
              className="reveal p-8 rounded-lg border border-white/10 bg-white/[0.02] hover:border-green-500/30 hover:bg-white/[0.04] transition-colors"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`feature-${i}`}
            >
              <f.icon className="w-6 h-6 text-green-400 mb-4" />
              <h3 className="font-display text-xl font-semibold mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Belt ladder */}
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-3">
            The Path
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
            Eight belts.
            <br />
            One cardiologist.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {RANKS.map((r, i) => (
            <div
              key={r.name}
              className="reveal p-5 rounded-md border border-white/10 bg-black/40 flex flex-col items-center text-center gap-3 hover:border-green-500/40 transition-colors"
              style={{ animationDelay: `${i * 50}ms` }}
              data-testid={`rank-preview-${i + 1}`}
            >
              <NinjaBadge level={i + 1} size={72} />
              <div>
                <div className="font-mono text-[10px] text-slate-500">
                  LVL {String(i + 1).padStart(2, "0")}
                </div>
                <div className={`font-display font-semibold text-sm ${r.color}`}>
                  {r.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">
          <div className="font-mono">EKG·NINJA · Built for medical students who like to level up.</div>
        </div>
      </footer>
    </div>
  );
}
